import { apiBaseCandidates, stagingApiConfigured } from './apiOriginPolicy';
const API_BASE_STORAGE_KEY = 'xph_api_base_url';
const PROD_API_BASE_URL = 'https://api.xpherium.com/api';
const RELEASE_CHANNEL = process.env.REACT_APP_RELEASE_CHANNEL || '';
const ENV_API_BASE_URL = process.env.REACT_APP_API_URL || (RELEASE_CHANNEL === 'staging' ? '' : PROD_API_BASE_URL);
const LOCAL_API_CANDIDATES = ['http://localhost:5001/api', 'http://127.0.0.1:5001/api'];
const REQUEST_TIMEOUT_MS = 12000;

const normalizeBase = (value = '') => String(value || '').replace(/\/+$/, '');
const isLocalApiBase = (apiBase = '') => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(String(apiBase || ''));
const isDesktopRuntime = () => {
  if (typeof window === 'undefined') return false;
  if (Boolean(window.desktopAPI)) return true;
  const userAgent = String(window.navigator?.userAgent || '').toLowerCase();
  return userAgent.includes(' electron/') || userAgent.includes('electron');
};
const isExplicitLocalFallbackEnabled = () =>
  process.env.REACT_APP_ENABLE_LOCAL_API_FALLBACK === 'true';
const isLocalRuntimeHost = () => {
  if (typeof window === 'undefined') return false;
  const host = String(window.location?.hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1';
};
const shouldIncludeLocalCandidates = () => {
  // In Electron desktop, keep parity with web/prod by default.
  // Only allow localhost API candidates when explicitly enabled.
  if (isDesktopRuntime()) {
    return isExplicitLocalFallbackEnabled();
  }
  return isExplicitLocalFallbackEnabled() || isLocalRuntimeHost();
};

const getPreferredDefaultApiBase = () => {
  const normalizedEnvBase = normalizeBase(ENV_API_BASE_URL);
  // Desktop should stay aligned with web/prod account data unless local fallback is explicitly enabled.
  if (isDesktopRuntime() && isLocalApiBase(normalizedEnvBase) && !shouldIncludeLocalCandidates()) {
    return PROD_API_BASE_URL;
  }
  return normalizedEnvBase;
};

export const getApiBaseUrl = () => {
  const preferredDefault = normalizeBase(getPreferredDefaultApiBase());
  if (RELEASE_CHANNEL === 'staging') {
    if (!stagingApiConfigured(preferredDefault)) {
      throw new Error('Staging API origin is not configured.');
    }
    return preferredDefault;
  }
  try {
    const persisted = localStorage.getItem(API_BASE_STORAGE_KEY);
    if (persisted && /^https?:\/\//i.test(persisted)) {
      const normalizedPersisted = normalizeBase(persisted);
      if (isLocalApiBase(normalizedPersisted) && !shouldIncludeLocalCandidates()) {
        try {
          localStorage.setItem(API_BASE_STORAGE_KEY, preferredDefault);
        } catch {
          // Ignore storage write failures.
        }
        return preferredDefault;
      }
      return normalizedPersisted;
    }
  } catch {
    // Ignore storage access issues and use default base.
  }
  return preferredDefault;
};

export const buildApiUrl = (path = '') => {
  const normalizedPath = String(path || '').replace(/^\/+/, '');
  return `${getApiBaseUrl()}/${normalizedPath}`;
};

export const getApiBaseCandidates = () => {
  const localCandidates = shouldIncludeLocalCandidates() ? LOCAL_API_CANDIDATES : [];
  const preferred = normalizeBase(getPreferredDefaultApiBase());
  return apiBaseCandidates({
    channel: RELEASE_CHANNEL,
    preferred,
    current: getApiBaseUrl(),
    localCandidates,
    production: PROD_API_BASE_URL,
  });
};

export const rememberApiBaseUrl = (apiBase) => {
  try {
    if (!apiBase) return;
    const normalized = normalizeBase(apiBase);
    if (isLocalApiBase(normalized) && !shouldIncludeLocalCandidates()) return;
    localStorage.setItem(API_BASE_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage write issues.
  }
};

// Allow callers to opt into a longer timeout for endpoints that run a full
// plan inline (e.g. `missions/run-once`, BI deep-analysis), and to provide
// their own AbortSignal (e.g. to cancel on unmount). Falls back to the
// global REQUEST_TIMEOUT_MS for normal calls.
//
// `credentials: 'include'` is forced on so the browser sends the
// HttpOnly `xph_session` cookie set by the backend. This is half of
// Phase 1 of the JWT-storage migration (the other half is the backend
// setting the cookie alongside every token in the JSON body). With this
// in place, the cookie becomes the primary auth source on every request
// — the Authorization header is now belt-and-suspenders. CORS is
// already configured with `credentials: true` on the backend (see
// backend/server.js).
const withTimeout = async (url, options = {}) => {
  const { timeoutMs, signal: callerSignal, ...rest } = options;
  const effectiveTimeout = Number.isFinite(Number(timeoutMs)) && Number(timeoutMs) > 0
    ? Number(timeoutMs)
    : REQUEST_TIMEOUT_MS;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);
  const onCallerAbort = () => controller.abort();
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort();
    else callerSignal.addEventListener('abort', onCallerAbort);
  }
  try {
    return await fetch(url, {
      credentials: 'include',
      ...rest,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
    if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
  }
};

const isNetworkFailure = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('failed to fetch') || message.includes('network') || error?.name === 'AbortError';
};

const looksLikeNonApiHtml = (response) => {
  const contentType = String(response?.headers?.get('content-type') || '').toLowerCase();
  return contentType.includes('text/html');
};

// Broadcast that the server rejected our credentials so AuthContext can
// re-validate the session and bounce to sign-in if the token expired.
// Throttled: a dashboard full of widgets can 401 dozens of times at once.
let lastAuthExpiredNoticeAt = 0;
let lastCreditNoticeAt = 0;
const notifyCreditsExhausted = (response) => {
  const now = Date.now();
  if (now - lastCreditNoticeAt < 1500) return;
  lastCreditNoticeAt = now;
  response.clone().json().then((data) => {
    if (data?.code !== 'IN_MONTHLY_CREDITS_EXHAUSTED') return;
    window.dispatchEvent(new CustomEvent('xph-open-payg-popup', {
      detail: {
        reason: 'exhausted',
        status: {
          plan: data.plan,
          showContactSales: !!data.showContactSales,
          paygEligible: data.paygEligible !== false,
          monthlyCap: data.monthlyCap,
          usedMonth: data.usedMonth,
          state: 'exhausted',
        },
      },
    }));
  }).catch(() => {});
};
const notifyAuthExpired = () => {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) return;
    const now = Date.now();
    if (now - lastAuthExpiredNoticeAt < 15000) return;
    lastAuthExpiredNoticeAt = now;
    window.dispatchEvent(new CustomEvent('xph-auth-expired'));
  } catch {
    // Never let the notification break the actual API call.
  }
};

export const fetchWithApiFallback = async (path = '', options = {}) => {
  const normalizedPath = `/${String(path || '').replace(/^\/+/, '')}`;
  const bases = getApiBaseCandidates();
  let lastError = null;
  let lastResponse = null;

  for (const base of bases) {
    try {
      const response = await withTimeout(`${base}${normalizedPath}`, options);
      lastResponse = response;

      // Persist only healthy responses to avoid pinning to a wrong/misaligned backend.
      if (response.ok) {
        // Some wrong origins return index.html with 200 OK for unknown paths.
        // Skip those so we can continue trying API bases.
        if (looksLikeNonApiHtml(response)) {
          continue;
        }
        rememberApiBaseUrl(base);
        return response;
      }

      // Auth rejections are authoritative — the server understood the
      // request and refused the credentials. Retrying other bases would
      // just replay the Bearer token against localhost and spam the
      // console with blocked requests, so surface the 401/403 as-is.
      if (response.status === 401) {
        notifyAuthExpired();
        return response;
      }
      if (response.status === 403) {
        return response;
      }
      if (response.status === 402) {
        notifyCreditsExhausted(response);
        return response;
      }

      // Retry alternate bases when the endpoint looks mismatched or unavailable.
      if ([404, 500, 502, 503, 504].includes(response.status)) {
        continue;
      }

      return response;
    } catch (error) {
      lastError = error;
      if (!isNetworkFailure(error)) {
        throw error;
      }
    }
  }

  if (lastResponse) {
    return lastResponse;
  }
  throw lastError || new Error('Network error');
};

